import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { ExternalLink, Users, Heart, Eye, Wallet } from 'lucide-react';
import { IG_METRICS, TT_METRICS } from '../lib/insightMetrics';
import { stickyColorFor, clientColorFor } from '../lib/stickyColors';

const StatTile = ({ label, value, Icon, swatch }) => (
  <div className="bg-white/70 backdrop-blur rounded-2xl p-4" style={{ border: `1px solid ${swatch.border}` }}>
    <div className="flex items-center gap-2" style={{ color: swatch.tape }}><Icon className="w-4 h-4" /><span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span></div>
    <div className="mt-1.5 text-2xl font-semibold text-slate-900">{value}</div>
  </div>
);

const nfmt = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 1 }));

const OrganicPlatformCard = ({ label, metrics, data, swatch }) => {
  const values = data?.metrics || {};
  const hasAny = metrics.some((m) => values[m.key] != null && values[m.key] !== '');
  return (
    <div className="bg-white rounded-2xl overflow-hidden" style={{ border: `1px solid ${swatch.border}` }}>
      <div className="px-5 py-3 text-xs font-semibold uppercase tracking-widest" style={{ background: swatch.bg, borderBottom: `1px solid ${swatch.border}`, color: swatch.ink }}>{label}</div>
      {!hasAny ? (
        <div className="p-6 text-center text-sm text-slate-400">no data logged yet</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4">
          {metrics.map((m) => {
            const v = values[m.key];
            if (v == null || v === '') return null;
            return (
              <div key={m.key} className="rounded-xl px-3 py-2.5" style={{ background: swatch.bg, opacity: 0.9 }}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{m.label}</div>
                <div className="mt-0.5 text-lg font-semibold text-slate-900">{nfmt(v)}{m.unit}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PublicInsights = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [sortKey, setSortKey] = useState('likes');
  const swatch = useMemo(() => stickyColorFor(clientColorFor(data?.client)), [data]);

  useEffect(() => {
    const params = {};
    const mode = searchParams.get('mode');
    const month = searchParams.get('month');
    const campaignId = searchParams.get('campaign_id');
    if (mode) params.mode = mode;
    if (month) params.month = month;
    if (campaignId) params.campaign_id = campaignId;
    api.get(`/public/insights/${token}`, { params }).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [token, searchParams]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400" data-testid="public-insights-error">this report link is no longer valid</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">loading…</div>;

  const { client, stats, organic } = data;
  const hasGmv = stats?.has_affiliate;
  const sortedContent = stats ? [...stats.content].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0)) : [];
  const SORT_OPTIONS = [
    { key: 'likes', label: 'likes' },
    { key: 'views', label: 'views' },
    ...(hasGmv ? [{ key: 'gmv', label: 'gmv' }] : []),
  ];

  return (
    <div className="min-h-screen p-6 lg:p-10" style={{ background: `linear-gradient(135deg, ${swatch.bg}, #ffffff 55%, ${swatch.bg})` }} data-testid="public-insights-page">
      <div className="max-w-4xl mx-auto">
        <header className="flex items-center gap-3 mb-8">
          <span className="text-3xl">{client.logo || '📦'}</span>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">{client.name}</h1>
            <p className="text-sm text-slate-500">
              {organic ? `organic social · ${new Date(organic.month + '-01').toLocaleString('en-GB', { month: 'long', year: 'numeric' })}` : 'mailer performance report'}
              {organic && stats?.campaigns_count > 0 ? ` · plus ${stats.campaigns_count} mailer campaign${stats.campaigns_count === 1 ? '' : 's'}` : ''}
            </p>
          </div>
        </header>

        {organic && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8" data-testid="public-organic-section">
            <OrganicPlatformCard label="instagram" metrics={IG_METRICS} data={organic.instagram} swatch={swatch} />
            <OrganicPlatformCard label="tiktok" metrics={TT_METRICS} data={organic.tiktok} swatch={swatch} />
          </div>
        )}

        {stats && stats.campaigns_count > 0 && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="public-mailer-section">
              <StatTile label="creators sent" value={nfmt(stats.total_creators)} Icon={Users} swatch={swatch} />
              <StatTile label="creators posted" value={nfmt(stats.posted_count)} Icon={Users} swatch={swatch} />
              <StatTile label="% posted" value={`${nfmt(stats.pct_posted)}%`} Icon={Users} swatch={swatch} />
              {hasGmv && <StatTile label="total gmv" value={`£${nfmt(stats.total_gmv)}`} Icon={Wallet} swatch={swatch} />}
              <StatTile label="total ig follower reach" value={nfmt(stats.total_ig_followers)} Icon={Users} swatch={swatch} />
              <StatTile label="total tt follower reach" value={nfmt(stats.total_tt_followers)} Icon={Users} swatch={swatch} />
              <StatTile label="total likes" value={nfmt(stats.total_likes)} Icon={Heart} swatch={swatch} />
              <StatTile label="total views" value={nfmt(stats.total_views)} Icon={Eye} swatch={swatch} />
            </div>

            <div className="bg-white rounded-2xl overflow-hidden" style={{ border: `1px solid ${swatch.border}` }}>
              <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-2" style={{ background: swatch.bg, borderBottom: `1px solid ${swatch.border}` }}>
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: swatch.ink }}>best performing content</span>
                <div className="inline-flex items-center gap-1" data-testid="public-mailer-content-sort">
                  <span className="text-[10px] uppercase tracking-widest mr-1" style={{ color: swatch.ink, opacity: 0.6 }}>sort by</span>
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortKey(opt.key)}
                      className="text-[11px] px-2 py-1 rounded-full transition-colors"
                      style={sortKey === opt.key ? { background: swatch.tape, color: '#fff' } : { background: '#fff', color: swatch.ink }}
                      data-testid={`public-mailer-content-sort-${opt.key}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {stats.content.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">no content posted yet</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {sortedContent.map((c, i) => (
                    <li key={i} className="flex items-center gap-3 px-5 py-3 text-sm flex-wrap">
                      <span className="text-slate-400 w-6 text-right">{i + 1}</span>
                      <span className="capitalize text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{c.platform}</span>
                      <span className="flex-1 min-w-0 truncate text-slate-700">{c.creator}</span>
                      <span className="text-slate-500 text-xs">{nfmt(c.likes)} likes</span>
                      <span className="text-slate-500 text-xs">{nfmt(c.views)} views</span>
                      {c.gmv != null && <span className="text-slate-500 text-xs">£{nfmt(c.gmv)} gmv</span>}
                      <a href={c.link} target="_blank" rel="noopener noreferrer" style={{ color: swatch.tape }}><ExternalLink className="w-3.5 h-3.5" /></a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        {!organic && (!stats || stats.campaigns_count === 0) && (
          <div className="bg-white border border-dashed rounded-2xl p-12 text-center text-sm text-slate-400" style={{ borderColor: swatch.border }}>no report data yet</div>
        )}

        <p className="text-center text-[11px] text-slate-400 mt-8">shared read-only report · lve social</p>
      </div>
    </div>
  );
};

export default PublicInsights;
